/**
 * "Report an AI result" — App Review 1.2 (revised 16 Oct 2026): an app that
 * shows AI-generated content gives the user a way to report it. ZoneIn has no
 * user-to-user content and no backend inbox, so a report is an email to the
 * founder, prefilled with what the AI produced, that the user sends (or not)
 * from their own mail app. Nothing is sent by the app itself.
 *
 * 17 Oct 2026: zoneinai.com has NO MX record (MEASURED via MXToolbox) — mail
 * to support@ or info@ that domain goes nowhere. SUPPORT_EMAIL is the single
 * place to change when forwarding exists; privacy.html, terms.html and
 * guide.html (public/ and marketing/site/) carry the same address by hand —
 * report.test.ts fails until all six agree (grep "mailto:" before changing it).
 *
 * `mailto:` inside the Capacitor WebView is cancelled and handed to the OS
 * (read in @capacitor/ios WebViewDelegationHandler.swift and
 * @capacitor/android Bridge.launchIntent) — which, with no mail app set up,
 * is expected to do nothing visible (INFERRED from that code, not yet seen
 * on a phone). So every report surface also offers "Copy report"
 * and shows the address (CLAUDE.md §1: a fallback that works when the guess
 * doesn't, §2: no silent failure).
 */

import { APP_VERSION, ZONE_BY_ID, type Task } from '../types';

export const SUPPORT_EMAIL = 'chadquesnel@gmail.com';

/** Some mail clients truncate very long mailto: bodies; keep well under 2 KB. */
const BODY_LIMIT = 1600;
const TEXT_LIMIT = 300;

export interface ReportContext {
  /** 'ios' | 'android' | 'web' — from Capacitor.getPlatform() at the call site. */
  platform: string;
}

function clip(s: string, max: number): string {
  const t = s.replace(/\s+/g, ' ').trim();
  return t.length > max ? `${t.slice(0, max - 1)}…` : t;
}

/** The plain-text report: what the AI produced for this task, plus the
 *  app/platform line support needs. The user adds what was wrong at the top. */
export function reportText(task: Task, ctx: ReportContext, opts: { forEmail?: boolean } = { forEmail: true }): string {
  const lines: string[] = [
    // The prompt is for the email body only; in-app sending has its own
    // note field (18 Oct 2026: the first live report stored the prompt).
    ...(opts.forEmail === false
      ? []
      : ['What was wrong with this result?', '(Your note here — e.g. wrong zone, steps make no sense, something upsetting.)', '']),
    '— AI result —',
    `Task: ${clip(task.text, TEXT_LIMIT)}`,
    `Zone: ${ZONE_BY_ID[task.zone]?.title ?? task.zone}`,
  ];
  if (task.steps.length > 0) {
    lines.push('Plan:');
    for (const [i, s] of task.steps.entries()) {
      lines.push(`${i + 1}. ${clip(s.text, TEXT_LIMIT)} (${s.minutes} min)`);
    }
  }
  lines.push('', `ZoneIn AI ${APP_VERSION} · ${ctx.platform} · task ${task.id}`);
  const full = lines.join('\n');
  return full.length > BODY_LIMIT ? `${full.slice(0, BODY_LIMIT - 1)}…` : full;
}

export function reportSubject(task: Task): string {
  return `ZoneIn AI: reporting an AI result — ${clip(task.text, 60)}`;
}

/** The mailto: URL. Absolute by construction, so it is safe on iOS. */
export function reportMailto(task: Task, ctx: ReportContext): string {
  return (
    `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(reportSubject(task))}` +
    `&body=${encodeURIComponent(reportText(task, ctx))}`
  );
}

export const REPORT_COPY = {
  link: 'Report an AI result',
  intro: 'Sends this task’s AI result to the ZoneIn team from inside the app, with your note. Nothing is sent until you tap Send report.',
  notePlaceholder: 'What was wrong? (optional) — e.g. wrong zone, steps make no sense, something offensive',
  send: 'Send report',
  sending: 'Sending…',
  sent: 'Thanks — your report was sent. We use reports to improve and filter the AI.',
  orEmail: `Or email it to ${SUPPORT_EMAIL}:`,
  email: 'Email the report',
  copy: 'Copy report',
  copied: 'Copied — paste it into any email to',
} as const;

/**
 * In-app sending (18 Oct 2026) — Google Play's AI-Generated Content policy
 * asks for reporting "without needing to exit the app"; the mail app is
 * outside it. The `report` function sits beside the relay (same project and
 * region), so its URL is derived from VITE_AI_RELAY_URL like deleteAccount's.
 */
const DEFAULT_REPORT_URL = 'https://us-central1-zonein-82a22.cloudfunctions.net/report';

export function reportUrl(relayUrl: string | undefined): string {
  const relay = (relayUrl ?? '').trim();
  if (/^https:\/\/[^/]+\/ai$/.test(relay)) return relay.replace(/\/ai$/, '/report');
  return DEFAULT_REPORT_URL;
}

export interface ReportPayload {
  text: string;
  note: string;
  platform: string;
  version: string;
  taskId: string;
}

/** Exactly what the server receives — reportPolicy.validateReport keeps these five. */
export function reportPayload(task: Task, ctx: ReportContext, note: string): ReportPayload {
  return {
    text: reportText(task, ctx, { forEmail: false }),
    note: note.trim().slice(0, 1000),
    platform: ctx.platform,
    version: APP_VERSION,
    taskId: task.id,
  };
}

/** The server's reply in words; unknown failures keep their detail (CLAUDE.md §2). */
export function describeReportFailure(status: number, body: unknown): string {
  const b = (body && typeof body === 'object' ? body : {}) as { error?: unknown; detail?: unknown };
  const error = typeof b.error === 'string' ? b.error : '';
  const detail = typeof b.detail === 'string' ? ` (${b.detail})` : '';
  if (status === 0) return 'No connection — the report was not sent. Try again, or email it below.';
  return `${error || `The report was not sent (HTTP ${status}).`}${detail}`;
}
